import {
    KDSCapableOrderProcessorData,
    KvdDocketButtonType,
    KvdDocketTimerType
} from "../order_processor/order-processor.data";
import {collection, field, transientField} from "@fedeltapointofsale/data-client";
import {TableServiceAreaInfo} from "../../pos/table-service-area/table-service-area.data";
import {MetaTagDefinitionReferenceData} from "../../catalogue/meta-tag/meta-tag.data";

export class KitchenDisplayData extends KDSCapableOrderProcessorData<KitchenDisplayData> {
    @field groupExtra: boolean = true;
    @field autoBumpsPreparedDockets: boolean = false;
    @field printOnBump: boolean = false;
    @field printOrderBarcode: boolean = false;
    @field showDocketCountStats: boolean = true;
    @field showOnTimeCountStats: boolean = false;
    @field showRemakeCountStats: boolean = false;
    @field showRushCountStats: boolean = false;
    @field showVoidCountStats: boolean = false;
    @field showDisplayedTimeStats: boolean = true;
    @field showAgeOnBumpStats: boolean = true;
    @field showWaitingTimeStats: boolean = false;
    @field showMakingTimeStats: boolean = false;
    @field showPreparedTimeStats: boolean = false;
    @field showOpenDockets: boolean = false;
    @field sendTakeawayOrderReadyNotification: boolean = true;
    @field sendTableOrderReadyNotification: boolean = false;
    @field notificationDescription: string;
    @field notificationLocation: string;
    @field displayTakeawayOrderReady: boolean = false;
    @field displayTableOrderReady: boolean = false;
    @collection(MetaTagDefinitionReferenceData) orderReadyQualifiers: MetaTagDefinitionReferenceData[];
    @field firstThresholdMins: number;
    @field secondThresholdMins: number;
    @field includeCompositeProductSummary: boolean = true;
    @field includeProductSummary: boolean = true;
    @field includeExtraSummary: boolean = false;
    @field kvdDocketTimerType: KvdDocketTimerType = KvdDocketTimerType.ORDER;
    @field kvdDocketButtonType: KvdDocketButtonType = KvdDocketButtonType.TRANSITION;
    @field highestLevelDisplayItems: number = 0;
    @field highestLevelManageItems: number = 0;
    @field lowestLevelDisplayItems: number = 2;
    @field lowestLevelManageItems: number = 1;
    @collection(TableServiceAreaInfo) tableServiceAreas: TableServiceAreaInfo[];

    @transientField get displayCompositeProduct(): boolean {
        return this.highestLevelDisplayItems === 0;
    }

    set displayCompositeProduct(displayCompositeProduct: boolean) {
        if (displayCompositeProduct !== this.displayCompositeProduct) {
            if (displayCompositeProduct) {
                this.highestLevelDisplayItems = 0;
            } else {
                this.highestLevelDisplayItems = 1;
                if (this.lowestLevelDisplayItems == 0) {
                    this.lowestLevelDisplayItems = 1;
                }
            }
            this.ensureNotManaged();
        }
    }

    @transientField get displayProduct(): boolean {
        return this.highestLevelDisplayItems <= 1 && this.lowestLevelDisplayItems >= 1;
    }

    set displayProduct(displayProduct: boolean) {
        if (displayProduct !== this.displayProduct) {
            if (displayProduct) {
                if (this.highestLevelDisplayItems === 0) {
                    this.lowestLevelDisplayItems = 1;
                } else {
                    this.highestLevelDisplayItems = 1;
                }
            } else {
                if (this.highestLevelDisplayItems === 0) {
                    this.lowestLevelDisplayItems = 0;
                } else if (this.lowestLevelDisplayItems === 2) {
                    this.highestLevelDisplayItems = 2;
                } else {
                    this.lowestLevelDisplayItems = 0;
                    this.highestLevelDisplayItems = 0;
                }
            }
            this.ensureNotManaged();
        }
    }

    @transientField get displayExtra(): boolean {
        return this.lowestLevelDisplayItems === 2;
    }

    set displayExtra(displayExtra: boolean) {
        if (displayExtra !== this.displayExtra) {
            if (displayExtra) {
                this.lowestLevelDisplayItems = 2;
            } else {
                this.lowestLevelDisplayItems = 1;
                if (this.highestLevelDisplayItems == 2) {
                    this.highestLevelDisplayItems = 1;
                }
            }
            this.ensureNotManaged();
        }
    }

    @transientField get manageCompositeProduct(): boolean {
        return this.highestLevelManageItems === 0;
    }

    set manageCompositeProduct(manageCompositeProduct: boolean) {
        if (manageCompositeProduct !== this.manageCompositeProduct) {
            if (manageCompositeProduct) {
                this.highestLevelManageItems = 0;
            } else {
                this.highestLevelManageItems = 1;
                if (this.lowestLevelManageItems == 0) {
                    this.lowestLevelManageItems = 1;
                }
            }
            this.ensureDisplayed();
        }
    }

    @transientField get manageProduct(): boolean {
        return this.highestLevelManageItems <= 1 && this.lowestLevelManageItems >= 1;
    }

    set manageProduct(manageProduct: boolean) {
        if (manageProduct !== this.manageProduct) {
            if (manageProduct) {
                if (this.highestLevelManageItems === 0) {
                    this.lowestLevelManageItems = 1;
                } else {
                    this.highestLevelManageItems = 1;
                }
            } else {
                if (this.highestLevelManageItems === 0) {
                    this.lowestLevelManageItems = 0;
                } else if (this.lowestLevelManageItems === 2) {
                    this.highestLevelManageItems = 2;
                } else {
                    this.lowestLevelManageItems = 0;
                    this.highestLevelManageItems = 0;
                }
            }
            this.ensureDisplayed();
        }
    }

    @transientField get manageExtra(): boolean {
        return this.lowestLevelManageItems === 2;
    }

    set manageExtra(manageExtra: boolean) {
        if (manageExtra !== this.manageExtra) {
            if (manageExtra) {
                this.lowestLevelManageItems = 2;
                this.displayExtra = true;
            } else {
                this.lowestLevelManageItems = 1;
                if (this.highestLevelManageItems == 2) {
                    this.highestLevelManageItems = 1;
                }
            }
            this.ensureDisplayed();
        }
    }

    private ensureNotManaged(): void {
        if (!this.displayCompositeProduct) {
            this.manageCompositeProduct = false;
        }
        if (!this.displayProduct) {
            this.manageProduct = false;
        }
        if (!this.displayExtra) {
            this.manageExtra = false;
        }
    }

    private ensureDisplayed(): void {
        if (this.manageCompositeProduct) {
            this.displayCompositeProduct = true;
        }
        if (this.manageProduct) {
            this.displayProduct = true;
        }
        if (this.manageExtra) {
            this.displayExtra = true;
        }
    }
}
